/**
 * 组织管理API服务
 * 提供组织的CRUD操作、组织树查询、用户分配等功能
 */

import apiClient from "./axios"; // 使用规范要求的apiClient

export const organizationAPI = {
    /**
     * 获取组织列表
     * @param {Object} params - 查询参数
     * @param {number} params.parent_id - 父组织ID
     * @param {string} params.search_term - 搜索关键词
     * @param {number} params.page - 页码
     * @param {number} params.page_size - 每页数量
     * @returns {Promise} API响应
     */
    getOrganizations: async (params = {}) => {
        const response = await apiClient.get("/v1/organizations", { params });
        return response.data;
    },

    /**
     * 获取组织树结构
     * @param {number} rootId - 根组织ID，可选
     * @returns {Promise} API响应
     */
    getOrganizationTree: async (rootId = null) => {
        const params = rootId ? { root_id: rootId } : {};
        const response = await apiClient.get("/v1/organizations/tree", { params });
        return response.data;
    },

    /**
     * 获取单个组织信息
     * @param {number} organizationId - 组织ID
     * @returns {Promise} API响应
     */
    getOrganization: async (organizationId) => {
        const response = await apiClient.get(`/v1/organizations/${organizationId}`);
        return response.data;
    },

    /**
     * 创建组织
     * @param {Object} organizationData - 组织数据
     * @param {string} organizationData.name - 组织名称
     * @param {string} organizationData.description - 组织描述
     * @param {number} organizationData.parent_id - 父组织ID
     * @returns {Promise} API响应
     */
    createOrganization: async (organizationData) => {
        const response = await apiClient.post("/v1/organizations", organizationData);
        return response.data;
    },

    /**
     * 更新组织信息
     * @param {number} organizationId - 组织ID
     * @param {Object} organizationData - 更新的组织数据
     * @returns {Promise} API响应
     */
    updateOrganization: async ({ id, data }) => {
        const response = await apiClient.put(`/v1/organizations/${id}`, data);
        return response.data;
    },

    /**
     * 删除组织
     * @param {number} organizationId - 组织ID
     * @returns {Promise} API响应
     */
    deleteOrganization: async (organizationId) => {
        const response = await apiClient.delete(`/v1/organizations/${organizationId}`);
        return response.data;
    },

    /**
     * 为用户分配组织
     * @param {number} organizationId - 组织ID
     * @param {number} userId - 用户ID
     * @returns {Promise} API响应
     */
    assignUserToOrganization: async (organizationId, userId) => {
        const response = await apiClient.post(`/v1/organizations/${organizationId}/users/${userId}`, null);
        return response.data;
    },

    /**
     * 从组织中移除用户
     * @param {number} organizationId - 组织ID
     * @param {number} userId - 用户ID
     * @returns {Promise} API响应
     */
    removeUserFromOrganization: async (organizationId, userId) => {
        const response = await apiClient.delete(`/v1/organizations/${organizationId}/users/${userId}`);
        return response.data;
    },

    /**
     * 获取组织的用户列表
     * @param {number} organizationId - 组织ID
     * @returns {Promise} API响应
     */
    getOrganizationUsers: async (organizationId) => {
        const response = await apiClient.get(`/v1/organizations/${organizationId}/users`);
        return response.data;
    },

    /**
     * 获取用户的组织列表
     * @param {number} userId - 用户ID
     * @returns {Promise} API响应
     */
    getUserOrganizations: async (userId) => {
        const response = await apiClient.get(`/v1/organizations/users/${userId}/organizations`);
        return response.data;
    },

    /**
     * 获取用户可访问的组织列表（包含子孙组织）
     * @param {number} userId - 用户ID
     * @returns {Promise} API响应
     */
    getUserAccessibleOrganizations: async (userId) => {
        const response = await apiClient.get(`/v1/organizations/users/${userId}/accessible-organizations`);
        return response.data;
    },

    /**
     * 设置用户的默认组织
     * @param {number} userId - 用户ID
     * @param {number} organizationId - 组织ID
     * @returns {Promise} API响应
     */
    setUserDefaultOrganization: async (userId, organizationId) => {
        const response = await apiClient.put(`/v1/organizations/users/${userId}/default-organization`, {
            organization_id: organizationId
        });
        return response.data;
    },

    /**
     * 获取用户的默认组织
     * @param {number} userId - 用户ID
     * @returns {Promise} API响应
     */
    getUserDefaultOrganization: async (userId) => {
        const response = await apiClient.get(`/v1/organizations/users/${userId}/default-organization`);
        return response.data;
    },
};

export default organizationAPI;
